// The firm's country (Settings > Company, owner 2026-10-24): Canada, United
// States, Australia or United Kingdom. Client pages use that country's words
// and number formats; nothing explains the switch to users (owner). The value
// lives in the firm's address (Location.LocationAddress.Country), which older
// firms typed freely ("Canada", "CA", "USA"...), so it is read loosely and
// anything unknown counts as Canada (every firm before this was Canadian).

export type CountryCode = "CA" | "US" | "AU" | "GB";

export const COUNTRY_CODES: CountryCode[] = ["CA", "US", "AU", "GB"];

export type Region = {
  code: CountryCode;
  /** Saved in the firm's address. */
  name: string;
  flag: string;
  /** Province / State / County. */
  regionLabel: string;
  postalLabel: string;
  cityLabel: string;
  /** null = typed freely (UK counties). */
  regions: { value: string; label: string }[] | null;
  /** Individual's tax number (SIN, SSN, TFN, NI number). */
  personalId: { label: string; placeholder: string; invalid: string };
  /** Organization's number (Business Number, EIN, ABN, company number). */
  businessId: { label: string; placeholder: string; rule: string };
  /** The GST/HST fields of an organization. */
  salesTaxLabel: string;
  salesTaxPeriodLabel: string;
  taxAgency: string;
};

export const CA_PROVINCES = [
  ["AB", "Alberta"],
  ["BC", "British Columbia"],
  ["MB", "Manitoba"],
  ["NB", "New Brunswick"],
  ["NL", "Newfoundland and Labrador"],
  ["NS", "Nova Scotia"],
  ["NT", "Northwest Territories"],
  ["NU", "Nunavut"],
  ["ON", "Ontario"],
  ["PE", "Prince Edward Island"],
  ["QC", "Quebec"],
  ["SK", "Saskatchewan"],
  ["YT", "Yukon"],
].map(([value, label]) => ({ value, label }));

export const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"],
  ["DE", "Delaware"], ["DC", "District of Columbia"], ["FL", "Florida"],
  ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"],
  ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"],
  ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"],
  ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"],
  ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"],
  ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"], ["PR", "Puerto Rico"],
].map(([value, label]) => ({ value, label }));

export const AU_STATES = [
  ["ACT", "Australian Capital Territory"],
  ["NSW", "New South Wales"],
  ["NT", "Northern Territory"],
  ["QLD", "Queensland"],
  ["SA", "South Australia"],
  ["TAS", "Tasmania"],
  ["VIC", "Victoria"],
  ["WA", "Western Australia"],
].map(([value, label]) => ({ value, label }));

export const REGIONS: Record<CountryCode, Region> = {
  CA: {
    code: "CA",
    name: "Canada",
    flag: "🇨🇦",
    regionLabel: "Province",
    postalLabel: "Postal code",
    cityLabel: "City",
    regions: CA_PROVINCES,
    personalId: {
      label: "SIN Number",
      placeholder: "000-000-000",
      invalid: "Invalid SIN format",
    },
    businessId: {
      label: "Business Number",
      placeholder: "9-digit business number",
      rule: "Business number must be 9 digits",
    },
    salesTaxLabel: "GST/HST Number",
    salesTaxPeriodLabel: "Reporting Period",
    taxAgency: "CRA",
  },
  US: {
    code: "US",
    name: "United States",
    flag: "🇺🇸",
    regionLabel: "State",
    postalLabel: "ZIP code",
    cityLabel: "City",
    regions: US_STATES,
    personalId: {
      label: "SSN",
      placeholder: "000-00-0000",
      invalid: "Invalid SSN format",
    },
    businessId: {
      label: "EIN",
      placeholder: "00-0000000",
      rule: "EIN must be 9 digits",
    },
    salesTaxLabel: "Sales tax ID",
    salesTaxPeriodLabel: "Filing frequency",
    taxAgency: "IRS",
  },
  AU: {
    code: "AU",
    name: "Australia",
    flag: "🇦🇺",
    regionLabel: "State",
    postalLabel: "Postcode",
    cityLabel: "City",
    regions: AU_STATES,
    personalId: {
      label: "TFN",
      placeholder: "000 000 000",
      invalid: "A TFN has 8 or 9 digits",
    },
    businessId: {
      label: "ABN",
      placeholder: "00 000 000 000",
      rule: "ABN must be 11 digits",
    },
    salesTaxLabel: "GST registration",
    salesTaxPeriodLabel: "BAS reporting period",
    taxAgency: "ATO",
  },
  GB: {
    code: "GB",
    name: "United Kingdom",
    flag: "🇬🇧",
    regionLabel: "County",
    postalLabel: "Postcode",
    cityLabel: "Town / City",
    regions: null,
    personalId: {
      label: "NI number",
      placeholder: "QQ 12 34 56 C",
      invalid: "Invalid National Insurance number",
    },
    businessId: {
      label: "Company number",
      placeholder: "8 characters, e.g. 01234567",
      rule: "Company number must be 8 letters or digits",
    },
    salesTaxLabel: "VAT number",
    salesTaxPeriodLabel: "VAT return period",
    taxAgency: "HMRC",
  },
};

/** Reads a saved country loosely; unknown or empty = Canada. */
export const countryCodeOf = (raw?: string | null): CountryCode => {
  const t = (raw || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!t) return "CA";
  if (/^(us|usa|unitedstates(ofamerica)?|america)/.test(t)) return "US";
  if (/^(au|aus|australia)/.test(t)) return "AU";
  if (
    /^(uk|gb|gbr|unitedkingdom|greatbritain|britain|england|scotland|wales|northernireland)/.test(
      t,
    )
  )
    return "GB";
  return "CA";
};

export const regionOf = (raw?: string | null): Region =>
  REGIONS[countryCodeOf(raw)];

/** Full name for a saved province/state code ("BC" -> "British Columbia"). */
export const regionName = (region: Region, value?: string | null) => {
  const v = (value || "").trim();
  const hit = region.regions?.find(
    (r) => r.value.toLowerCase() === v.toLowerCase(),
  );
  return hit ? hit.label : v;
};

// --- Individual's tax number ------------------------------------------------

/** Formats while typing: SIN 000-000-000, SSN 000-00-0000, TFN 000 000 000,
 * NI QQ123456C (letters upper-case, spaces dropped). */
export const formatPersonalId = (code: CountryCode, text: string): string => {
  if (code === "GB")
    return (text || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 9);
  const d = (text || "").replace(/\D/g, "").slice(0, 9);
  const groups =
    code === "US" ? [3, 2, 4] : code === "AU" ? [3, 3, 3] : [3, 3, 3];
  const sep = code === "AU" ? " " : "-";
  const parts: string[] = [];
  let i = 0;
  for (const g of groups) {
    if (i >= d.length) break;
    parts.push(d.slice(i, i + g));
    i += g;
  }
  return parts.join(sep);
};

/** Empty is fine; otherwise the country's complete format. */
export const personalIdPattern = (code: CountryCode): RegExp =>
  code === "US"
    ? /^\d{3}-\d{2}-\d{4}$/
    : code === "AU"
      ? /^\d{3} \d{3} \d{2,3}$/
      : code === "GB"
        ? /^[A-Z]{2}\d{6}[A-D]$/
        : /^\d{3}-\d{3}-\d{3}$/;

export const personalIdMaxLength = (code: CountryCode) =>
  code === "US" ? 11 : code === "GB" ? 9 : 11;

// --- Organization's number --------------------------------------------------

/** Formats while typing / on paste: BN 9 digits ("123456789 RT0001" keeps the
 * BN), EIN 00-0000000, ABN 11 digits shown 00 000 000 000, UK company number
 * 8 letters/digits upper-case. */
export const formatBusinessId = (code: CountryCode, text: string): string => {
  const raw = text || "";
  if (code === "GB")
    return raw
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 8);
  if (code === "US") {
    const d = raw.replace(/\D/g, "").slice(0, 9);
    return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
  }
  if (code === "AU") {
    const d = raw.replace(/\D/g, "").slice(0, 11);
    return [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8), d.slice(8, 11)]
      .filter(Boolean)
      .join(" ");
  }
  return raw.replace(/\D/g, "").slice(0, 9);
};

/** "" = fine. `saved` = an older stored value sent back unchanged (accepted). */
export const businessIdProblem = (
  code: CountryCode,
  text?: string | null,
  saved?: string | null,
): string => {
  const value = (text || "").trim();
  if (!value || isCompleteBusinessId(code, value)) return "";
  if (saved != null && value === (saved || "").trim()) return "";
  return REGIONS[code].businessId.rule;
};

export const isCompleteBusinessId = (code: CountryCode, text: string) => {
  const v = (text || "").trim();
  if (code === "US") return /^\d{2}-\d{7}$/.test(v);
  if (code === "AU") return /^\d{2} \d{3} \d{3} \d{3}$/.test(v);
  if (code === "GB") return /^[A-Z0-9]{8}$/.test(v);
  return /^\d{9}$/.test(v);
};

export const businessIdMaxLength = (code: CountryCode) =>
  code === "US" ? 10 : code === "AU" ? 14 : code === "GB" ? 8 : 9;
